/**
 * Display rules for the salesperson profile — pure, so they are unit-tested
 * without a browser. Money arrives as integer-Rial numbers/strings and is
 * rendered through the shared formatter in the tenant's display unit.
 */
import type { InsightUnit, InsightVerdict } from '../../lib/types/commission.ts';

export type AmountUnit = 'rial' | 'toman';

const faDigits = (s: string): string => s.replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)] as string);

/** Whole-number grouping with Persian digits (no money semantics). */
export const faInt = (n: number): string =>
  faDigits(Math.round(n).toLocaleString('en-US')).replace(/,/g, '٬');

export function formatDuration(seconds: number | null): string {
  if (seconds === null) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return faDigits(`${m}:${String(s).padStart(2, '0')}`);
}

/** permille (0–1000) → «۶۲٫۵٪» */
export function formatPermille(value: number): string {
  const whole = Math.floor(value / 10);
  const frac = value % 10;
  return `${faDigits(String(whole))}${frac ? `٫${faDigits(String(frac))}` : ''}٪`;
}

/** satisfaction ×100 → «۳٫۷ از ۵» */
export function formatScore100(value: number): string {
  const whole = Math.floor(value / 100);
  const tenth = Math.round((value % 100) / 10);
  const [w, t] = tenth === 10 ? [whole + 1, 0] : [whole, tenth];
  return `${faDigits(String(w))}${t ? `٫${faDigits(String(t))}` : ''} از ۵`;
}

export function formatInsightValue(
  value: number | null,
  unit: InsightUnit,
  rial: (digits: string) => string,
): string {
  if (value === null) return '—';
  switch (unit) {
    case 'rial':
      return rial(String(Math.round(value)));
    case 'count':
      return faInt(value);
    case 'permille':
      return formatPermille(Math.round(value));
    case 'seconds':
      return formatDuration(Math.round(value));
    case 'score100':
      return formatScore100(Math.round(value));
  }
}

export const VERDICT_LABEL: Readonly<Record<InsightVerdict, string>> = {
  strong: 'نقطهٔ قوت',
  weak: 'نقطهٔ ضعف',
  average: 'در حد میانگین',
  insufficient: 'داده ناکافی',
};

export const VERDICT_TONE: Readonly<Record<InsightVerdict, 'success' | 'danger' | 'neutral'>> = {
  strong: 'success',
  weak: 'danger',
  average: 'neutral',
  insufficient: 'neutral',
};

/** Width (0–100) of the "vs team average" bar; 100 % of average sits at 50 %. */
export function averageBarPercent(percentOfAverage: number | null): number {
  if (percentOfAverage === null) return 0;
  return Math.max(2, Math.min(100, Math.round(percentOfAverage / 2)));
}

export const OUTCOME_LABELS: Readonly<Record<string, string>> = {
  dealProgressed: 'پیشبرد معامله',
  resolved: 'حل‌شده',
  informationProvided: 'ارائهٔ اطلاعات',
  followUpNeeded: 'نیازمند پیگیری',
  unresolved: 'بی‌نتیجه',
  lost: 'از دست رفته',
  transferred: 'ارجاع‌شده',
};

export const SENTIMENT_LABELS: Readonly<Record<string, string>> = {
  positive: 'مثبت',
  neutral: 'خنثی',
  negative: 'منفی',
  mixed: 'متغیر',
};

export const ACTIVITY_TYPE_LABELS: Readonly<Record<string, string>> = {
  call: 'تماس',
  meeting: 'جلسه',
  email: 'ایمیل',
  task: 'کار',
  note: 'یادداشت',
};
